import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";
import UserProfile from "./UserProfile";

const Header = ({ onCategorySelect, onSearchChange, onCartOpen, onWishlistOpen, onDebug }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const { getCartCount } = useCart();
  const { getWishlistCount } = useWishlist();
  const navigate = useNavigate();

  const categories = {
    dresses: ["cotton", "rayon", "organza", "georgette", "satin"],
    sarees: ["cotton", "fancy", "banarasi", "silk", "georgette", "designer"]
  };

  // Materials that can be used across categories
  const materials = ["cotton", "rayon", "organza", "georgette", "satin", "silk"];

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    onSearchChange(e.target.value);
  };

  return (
    <header className="bg-white shadow-sm border-b">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Mobile menu button */}
          <button 
            className="lg:hidden p-2"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>

          {/* Logo */}
          <div className="flex items-center cursor-pointer" onClick={() => navigate('/')}>
            <img 
              src="/images/logos/Monvi_Styles_Brand_Identity_Design-removebg-preview (1).png" 
              alt="Monvi Styles" 
              className="h-12 w-auto"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
            <h1 className="text-2xl font-bold text-gray-900 ml-2" style={{ display: 'none' }}>MONVI</h1>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex space-x-8">
            <div className="relative group">
              <button 
                className="text-gray-700 hover:text-gray-900 px-3 py-2 font-medium"
                onClick={() => navigate('/dress')}
              >
                Dresses
              </button>
              <div className="absolute left-0 mt-2 w-48 bg-white rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50">
                {categories.dresses.map((material) => (
                  <button
                    key={material}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 capitalize"
                    onClick={() => navigate(`/dress/${material}`)}
                  >
                    {material}
                  </button>
                ))}
              </div>
            </div>
            <div className="relative group">
              <button 
                className="text-gray-700 hover:text-gray-900 px-3 py-2 font-medium"
                onClick={() => navigate('/saree')}
              >
                Sarees
              </button>
              <div className="absolute left-0 mt-2 w-48 bg-white rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50">
                {categories.sarees.map((sub) => (
                  <button
                    key={sub}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 capitalize"
                    onClick={() => navigate(`/saree/${sub}`)}
                  >
                    {sub}
                  </button>
                ))}
              </div>
            </div>
            <div className="relative group">
              <button 
                className="text-gray-700 hover:text-gray-900 px-3 py-2 font-medium"
                onClick={() => navigate('/material')}
              >
                Materials
              </button>
              <div className="absolute left-0 mt-2 w-48 bg-white rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50">
                {materials.map((material) => (
                  <button
                    key={material}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 capitalize"
                    onClick={() => navigate(`/material/${material}`)}
                  >
                    {material}
                  </button>
                ))}
              </div>
            </div>
            <button 
              className="text-gray-700 hover:text-gray-900 px-3 py-2 font-medium"
              onClick={() => navigate('/')}
            >
              Home
            </button>
            <button 
              className="text-gray-700 hover:text-gray-900 px-3 py-2 font-medium"
              onClick={onDebug}
            >
              Debug
            </button>
          </nav>

          {/* Search Bar */}
          <div className="hidden sm:flex items-center flex-1 max-w-lg mx-8">
            <div className="relative w-full">
              <input
                type="text"
                placeholder="Search products..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                value={searchTerm}
                onChange={handleSearchChange}
              />
              <svg className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>

          {/* Right Icons */}
          <div className="flex items-center space-x-4">
            <button 
              onClick={onWishlistOpen}
              className="text-gray-700 hover:text-gray-900 relative"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
              {getWishlistCount() > 0 && (
                <span className="absolute -top-2 -right-2 bg-pink-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {getWishlistCount()}
                </span>
              )}
            </button>
            <button 
              onClick={onCartOpen}
              className="text-gray-700 hover:text-gray-900 relative"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
              {getCartCount() > 0 && (
                <span className="absolute -top-2 -right-2 bg-pink-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {getCartCount()}
                </span>
              )}
            </button>
            <UserProfile />
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden bg-white border-t">
            <div className="px-4 py-2 space-y-1">
              <div className="mb-4">
                <input
                  type="text"
                  placeholder="Search products..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
              </div>
              <div>
                <button 
                  className="block w-full text-left px-3 py-2 text-base font-medium text-gray-700 hover:bg-gray-50"
                  onClick={() => navigate('/')}
                >
                  Home
                </button>
              </div>
              <div>
                <button 
                  className="block w-full text-left px-3 py-2 text-base font-medium text-gray-700 hover:bg-gray-50"
                  onClick={() => navigate('/dress')}
                >
                  Dresses
                </button>
                <div className="ml-4 space-y-1">
                  {categories.dresses.map((sub) => (
                    <button
                      key={sub}
                      className="block w-full text-left px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 capitalize"
                      onClick={() => navigate(`/dress/${sub}`)}
                    >
                      {sub}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <button 
                  className="block w-full text-left px-3 py-2 text-base font-medium text-gray-700 hover:bg-gray-50"
                  onClick={() => navigate('/saree')}
                >
                  Sarees
                </button>
                <div className="ml-4 space-y-1">
                  {categories.sarees.map((sub) => (
                    <button
                      key={sub}
                      className="block w-full text-left px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 capitalize"
                      onClick={() => navigate(`/saree/${sub}`)}
                    >
                      {sub}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <button 
                  className="block w-full text-left px-3 py-2 text-base font-medium text-gray-700 hover:bg-gray-50"
                  onClick={() => navigate('/material')}
                >
                  Materials
                </button>
                <div className="ml-4 space-y-1">
                  {materials.map((material) => (
                    <button
                      key={material}
                      className="block w-full text-left px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 capitalize"
                      onClick={() => navigate(`/material/${material}`)}
                    >
                      {material}
                  </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
